"use client"

import { useState, useEffect, useMemo } from "react"
import axios from "axios"
import { ArrowUpward, ArrowDownward, Edit, Delete } from "@mui/icons-material"
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Grid,
  FormControl,
  Select,
  MenuItem,
  TextField,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  CircularProgress,
  Alert,
} from "@mui/material"
import AddOrderModal from "../../components/PurchasesOrder/AddOrderModal"
import EditOrderModal from "../../components/PurchasesOrder/EditOrderModal"
import ConfirmationDialog from "../../components/Common/ConfirmationDialog"
import { useOutletContext } from "react-router-dom"
import CheckIcon from "@mui/icons-material/Check";
import BaseURL from "../../api/AppURL";

const PurchasePlanManage = () => {
  const [orders, setOrders] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [searchTerm, setSearchTerm] = useState("")
  const [entriesPerPage, setEntriesPerPage] = useState(25)
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "ascending" })
  const [downloading, setDownloading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)

  // Modal states
  const [addModalOpen, setAddModalOpen] = useState(false)
  const [editModalOpen, setEditModalOpen] = useState(false)
  const [currentOrderId, setCurrentOrderId] = useState(null)

  // Confirmation Dialog states
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [orderToDeleteId, setOrderToDeleteId] = useState(null)

  const { showSnackbar } = useOutletContext()

  const fetchOrders = async () => {
    setLoading(true)
    setError(null)
    try {
      const token = localStorage.getItem("auth_token")
      const response = await axios.get(`${BaseURL}/all/order`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      })
      const fetchedData = Array.isArray(response.data.orders) ? response.data.orders : []
      setOrders(fetchedData)
      console.log("Orders fetched successfully. Total items:", fetchedData.length)
    } catch (err) {
      console.error("Error fetching orders:", err)
      setError("Failed to load orders. Please try again later.")
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchOrders()
  }, [])

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value)
    setCurrentPage(1)
  }

  const handleEntriesChange = (event) => {
    setEntriesPerPage(event.target.value)
    setCurrentPage(1)
  }

  const requestSort = (key) => {
    let direction = "ascending"
    if (sortConfig.key === key && sortConfig.direction === "ascending") {
      direction = "descending"
    }
    setSortConfig({ key, direction })
  }

  const sortedOrders = useMemo(() => {
    const sortableItems = [...orders]
    if (sortConfig.key === null) return sortableItems

    sortableItems.sort((a, b) => {
      let aValue = a[sortConfig.key]
      let bValue = b[sortConfig.key]

      // Handle nested properties like customer.name or customer.mobile_no
      if (sortConfig.key === "customer_name") {
        aValue = a.customer?.name || ""
        bValue = b.customer?.name || ""
      } else if (sortConfig.key === "customer_mobile") {
        aValue = a.customer?.mobile_no || ""
        bValue = b.customer?.mobile_no || ""
      }

      if (aValue < bValue) {
        return sortConfig.direction === "ascending" ? -1 : 1
      }
      if (aValue > bValue) {
        return sortConfig.direction === "ascending" ? 1 : -1
      }
      return 0
    })
    return sortableItems
  }, [orders, sortConfig])

  const filteredOrders = useMemo(() => {
    return sortedOrders.filter((order) => {
      const searchLower = searchTerm.toLowerCase()
      const customerName = order.customer?.name || ""
      const customerMobile = order.customer?.mobile_no || ""

      return (
        String(order.id).toLowerCase().includes(searchLower) ||
        String(order.chalan_no || "")
          .toLowerCase()
          .includes(searchLower) ||
        customerName.toLowerCase().includes(searchLower) ||
        customerMobile.toLowerCase().includes(searchLower) ||
        String(order.sale_date || "")
          .toLowerCase()
          .includes(searchLower) ||
        String(order.delivery_date || "")
          .toLowerCase()
          .includes(searchLower)
      )
    })
  }, [sortedOrders, searchTerm])

  const indexOfLastOrder = currentPage * entriesPerPage
  const indexOfFirstOrder = indexOfLastOrder - entriesPerPage
  const displayedOrders = filteredOrders.slice(indexOfFirstOrder, indexOfLastOrder)
  const totalPages = Math.ceil(filteredOrders.length / entriesPerPage)

  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return null
    return sortConfig.direction === "ascending" ? <ArrowUpward fontSize="small" /> : <ArrowDownward fontSize="small" />
  }

  const handleNextPage = () => {
    setCurrentPage((prevPage) => Math.min(prevPage + 1, totalPages))
  }

  const handlePreviousPage = () => {
    setCurrentPage((prevPage) => Math.max(prevPage - 1, 1))
  }

  const totalEntries = filteredOrders.length
  const startEntry = totalEntries === 0 ? 0 : indexOfFirstOrder + 1
  const endEntry = Math.min(indexOfLastOrder, totalEntries)



  const handleOrdInvoiceDownload = async (orderId) => {
    try {
      const token = localStorage.getItem("auth_token")
      const response = await axios.get(
        `${BaseURL}/orders/invoice/download/${orderId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Accept: "application/pdf",
          },
          responseType: "blob",
        }
      )
      // Try to get filename from headers; fallback to default
      let filename = `invoice_${orderId}.pdf`
      const contentDisposition =
        response.headers["content-disposition"] || response.headers["Content-Disposition"]
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^"]+)"?/)
        if (match && match[1]) filename = match[1]
      }
      // Create a blob link and trigger download
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement("a")
      link.href = url
      link.setAttribute("download", filename)
      document.body.appendChild(link)
      link.click()
      link.parentNode.removeChild(link)
      window.URL.revokeObjectURL(url)
      showSnackbar("Invoice PDF downloaded!", "success")
    } catch (err) {
      showSnackbar("Failed to download invoice PDF.", "error")
    }
  }


  const renderPageNumbers = () => {
    const pageNumbers = []
    for (let i = 1; i <= totalPages; i++) {
      pageNumbers.push(
        <Button
          key={i}
          variant={currentPage === i ? "contained" : "outlined"}
          color={currentPage === i ? "primary" : "inherit"}
          onClick={() => setCurrentPage(i)}
          sx={{ minWidth: 40, px: 1 }}
        >
          {i}
        </Button>,
      )
    }
    return pageNumbers
  }

  const handleAddOrderClick = () => {
    setAddModalOpen(true)
  }

  const handleAddModalClose = () => {
    setAddModalOpen(false)
  }

  const handleOrderAddedSuccess = (message) => {
    showSnackbar(message, "success")
    fetchOrders()
  }

  const handleEditOrderClick = (orderId) => {
    setCurrentOrderId(orderId)
    setEditModalOpen(true)
  }

  const handleEditModalClose = () => {
    setEditModalOpen(false)
    setCurrentOrderId(null)
  }

  const handleOrderUpdatedSuccess = (message) => {
    showSnackbar(message, "success")
    fetchOrders()
  }

  // Open confirmation dialog
  const handleDeleteClick = (orderId) => {
    setOrderToDeleteId(orderId)
    setConfirmDialogOpen(true)
  }

  // Close confirmation dialog
  const handleConfirmDialogClose = () => {
    setConfirmDialogOpen(false)
    setOrderToDeleteId(null)
  }

  // Execute delete after confirmation
  const handleConfirmDelete = async () => {
    if (!orderToDeleteId) return

    setConfirmDialogOpen(false) // Close the dialog immediately
    console.log("Attempting to delete order with ID:", orderToDeleteId)

    try {
      const token = localStorage.getItem("auth_token")
      const response = await axios.delete(`${BaseURL}/delete/order/${orderToDeleteId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
      })

      console.log("DELETE API Response:", response.data)

      const isSuccess = response.data.success === true || response.data.message?.includes("successfully")

      if (isSuccess) {
        showSnackbar(response.data.message || "Order deleted successfully!", "success")
        console.log("Order deleted successfully. Optimistically updating UI...")

        setOrders((prevOrders) => {
          const updatedOrders = prevOrders.filter((s) => {
            const isMatch = String(s.id) === String(orderToDeleteId)
            console.log(
              `Comparing s.id: ${s.id} (type: ${typeof s.id}) with orderToDeleteId: ${orderToDeleteId} (type: ${typeof orderToDeleteId}). Match: ${isMatch}`,
            )
            return !isMatch
          })
          console.log("Orders after optimistic update. New count:", updatedOrders.length)
          return updatedOrders
        })

        await fetchOrders()
        console.log("Table re-synced with backend after deletion.")
      } else {
        showSnackbar(response.data.message || "Failed to delete order.", "error")
        console.log("Failed to delete order (API reported failure or unexpected response):", response.data.message)
      }
    } catch (err) {
      console.error("Error deleting order:", err)
      showSnackbar("An error occurred while deleting the order.", "error")
    } finally {
      setOrderToDeleteId(null)
    }
  }

  return (
    <Box>
      <Card sx={{ mb: 3 }}>
        <CardContent>
          {/* Action Buttons Section */}
          <Box sx={{ display: "flex", gap: 2, mb: 2, flexWrap: "wrap" }}>
            <Button variant="contained" onClick={handleAddOrderClick}>
              Add Orders
            </Button>

            <Button variant="contained">Orders Archive</Button>
            {/* ৳ 0 TK text with lighter style */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Typography variant="h7" sx={{ fontWeight: "normal", color: "text.secondary" }}>
                ৳ 0 TK
              </Typography>
            </Box>
          </Box>

          {/* Search and Entries Per Page Section */}
          <Grid container spacing={2} alignItems="center" sx={{ mb: 2 }}>
            <Grid item xs={12} sm={6} md={4}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Typography variant="body2">Show</Typography>
                <FormControl size="small" sx={{ minWidth: 80 }}>
                  <Select
                    value={entriesPerPage}
                    onChange={handleEntriesChange}
                    displayEmpty
                    inputProps={{ "aria-label": "Entries per page" }}
                  >
                    <MenuItem value={10}>10</MenuItem>
                    <MenuItem value={25}>25</MenuItem>
                    <MenuItem value={50}>50</MenuItem>
                    <MenuItem value={100}>100</MenuItem>
                  </Select>
                </FormControl>
                <Typography variant="body2">entries</Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6} md={8}>
              <TextField
                label="Search"
                variant="outlined"
                size="small"
                value={searchTerm}
                onChange={handleSearchChange}
                fullWidth
                sx={{ ml: { sm: "auto" } }}
              />
            </Grid>
          </Grid>

          {/* Table Section */}
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Alert severity="error">{error}</Alert>
          ) : (
            <TableContainer component={Paper} elevation={0} variant="outlined">
              <Box sx={{ overflowX: "auto", minWidth: 650 }}>
                <Table aria-label="orders table">
                  <TableHead sx={{ bgcolor: "#f5f5f5" }}>
                    <TableRow>
                      <TableCell
                        sx={{ fontWeight: "bold", width: "5%", borderRight: "1px solid #e0e0e0", whiteSpace: "nowrap" }}
                      >
                        Sl
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          width: "15%",
                          borderRight: "1px solid #e0e0e0",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Button
                          onClick={() => requestSort("id")}
                          sx={{ textTransform: "none", p: 0, minWidth: 0, whiteSpace: "nowrap" }}
                        >
                          Order No {getSortIcon("id")}
                        </Button>
                      </TableCell>

                      {/* MPO (Customer) changed to MPO */}
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          width: "25%",
                          borderRight: "1px solid #e0e0e0",
                          whiteSpace: "nowrap",
                          minWidth: "180px",
                        }}
                      >
                        <Button
                          onClick={() => requestSort("customer_name")}
                          sx={{ textTransform: "none", p: 0, minWidth: 0, whiteSpace: "nowrap" }}
                        >
                          MPO {getSortIcon("customer_name")}
                        </Button>
                      </TableCell>
                      <TableCell
                        sx={{
                          fontWeight: "bold",
                          width: "15%",
                          borderRight: "1px solid #e0e0e0",
                          whiteSpace: "nowrap",
                        }}
                      >
                        <Button
                          onClick={() => requestSort("sale_date")}
                          sx={{ textTransform: "none", p: 0, minWidth: 0, whiteSpace: "nowrap" }}
                        >
                          Order Date {getSortIcon("sale_date")}
                        </Button>
                      </TableCell>

                      <TableCell sx={{ fontWeight: "bold", width: "10%", whiteSpace: "nowrap" }}>Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {displayedOrders.length > 0 ? (
                      displayedOrders.map((order, index) => (
                        <TableRow key={order.id || index}>
                          <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>
                            {indexOfFirstOrder + index + 1}
                          </TableCell>
                          <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>ORD-{order.id}</TableCell>


                          {/* MPO column data display */}
                          <TableCell sx={{ borderRight: "1px solid #e0e0e0", whiteSpace: "nowrap" }}>
                            {order.customer?.name} ({order.customer?.mobile_no})
                          </TableCell>
                          <TableCell sx={{ borderRight: "1px solid #e0e0e0" }}>{order.order_date}</TableCell>

                          <TableCell>
                            <Box sx={{ display: "flex", gap: 1 }}>
                              <Button variant="outlined" size="small" onClick={() => handleEditOrderClick(order.id)}>
                                <Edit fontSize="small" />
                              </Button>

                              <Button
                                variant="contained"
                                size="small"
                                sx={{ backgroundColor: "#4CAF50", "&:hover": { backgroundColor: "#45a049" } }}
                                onClick={() => handleOrdInvoiceDownload(order.id)}
                              >
                                ORD
                                {order.dispatch === 1 && (
                                  <CheckIcon
                                    sx={{ fontSize: 24, color: "#fff", ml: 1, verticalAlign: "middle" }}
                                  />
                                )}
                              </Button>
                              {/* Delete button moved to the end */}
                              <Button
                                variant="contained"
                                color="error"
                                size="small"
                                onClick={() => handleDeleteClick(order.id)}
                              >
                                <Delete fontSize="small" />
                              </Button>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))
                    ) : (
                      <TableRow>
                        <TableCell colSpan={7} sx={{ textAlign: "center", py: 4 }}>
                          No orders found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </Box>
            </TableContainer>
          )}
          {/* Pagination Section */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              justifyContent: "space-between",
              alignItems: { xs: "center", sm: "center" },
              gap: 2,
              mt: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary">
              Showing {startEntry} to {endEntry} of {totalEntries} entries
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button variant="outlined" onClick={handlePreviousPage} disabled={currentPage === 1}>
                Prev
              </Button>
              {renderPageNumbers()}
              <Button variant="outlined" onClick={handleNextPage} disabled={currentPage === totalPages}>
                Next
              </Button>
            </Box>
          </Box>
        </CardContent>
      </Card>
      {/* Add Order Modal */}
      <AddOrderModal open={addModalOpen} onClose={handleAddModalClose} onSaveSuccess={handleOrderAddedSuccess} />
      {/* Edit Order Modal */}
      {editModalOpen && (
        <EditOrderModal
          open={editModalOpen}
          onClose={handleEditModalClose}
          onUpdateSuccess={handleOrderUpdatedSuccess}
          orderId={currentOrderId}
        />
      )}
      {/* Confirmation Dialog for Delete */}
      <ConfirmationDialog
        open={confirmDialogOpen}
        onClose={handleConfirmDialogClose}
        onConfirm={handleConfirmDelete}
        title="Are you sure?"
        message="Delete This Data?"
        confirmText="Yes, delete it!"
        cancelText="Cancel"
      />
    </Box>
  )
}

export default PurchasePlanManage
